"use client";

import { CitationTool } from "@/components/citation-tool";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const BLOG_POST_CITATION_GENERATOR_STRINGS = {
  howToCiteABlog: "How to Cite a Blog Post",
  n1EnterThePostDetails: "1. Enter the post details.",
  typeTheAuthorAScreen:
    "Type the author (a screen name is fine if that's how they publish), the post title, the blog's name, the exact publication date, and the URL. The blog name goes in its own field — don't fold it into the title.",
  n2PickAStyle: "2. Pick a style.",
  apa7thTreatsABlog:
    " APA 7th treats a blog like a periodical: the post title stays plain and the",
  blogNameIsItalicized: "blog name is italicized",
  notThePostMla9th:
    "— not the post. MLA 9th puts the post title in quotation marks with the blog name as the italicized container.",
  n3Generate: "3. Generate.",
  weBuildAFullBlog:
    " We build a full blog-post reference with the italics in the right place. Edit any field, then copy or export it to BibTeX, RIS, CSV, or Word.",
} as const;

/**
 * Live citation generator embedded on the Blog Post landing page. The tool opens straight
 * on the manual-entry tab preset to the "Blog post" source type (CSL `post-weblog`), so
 * the blog name lands in the container field that APA and MLA italicize. Users type the
 * author, post title, blog name, date, and URL and pick any of the 10,834 styles.
 */
function BlogPostGenerator() {
  return (
    <CitationTool
      initialManualType="post-weblog"
      initialMode="manual"
      styles={getFeaturedStyles()}
    />
  );
}

export default function BlogPostCitationGeneratorWidget() {
  const strings = useToolUi(BLOG_POST_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Live generator (manual entry preset to the Blog post source type) */}
      <BlogPostGenerator />
      {/* How to */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToCiteABlog}
        </h2>
        <ol className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.n1EnterThePostDetails}
            </strong>{" "}
            {strings.typeTheAuthorAScreen}
          </li>
          <li>
            <strong className="text-foreground">{strings.n2PickAStyle}</strong>
            {strings.apa7thTreatsABlog}{" "}
            <span className="font-medium text-foreground">
              {strings.blogNameIsItalicized}
            </span>{" "}
            {strings.notThePostMla9th}
          </li>
          <li>
            <strong className="text-foreground">{strings.n3Generate}</strong>
            {strings.weBuildAFullBlog}
          </li>
        </ol>
      </section>
    </>
  );
}

/**
 * English only for now. Add locales here as this page type is translated — the
 * registry in src/lib/i18n/localized-routes.ts has to agree, or the proxy will
 * redirect the URL this generates away again.
 */
export function generateStaticParams() {
  return [{ locale: "en" }];
}

export const dynamicParams = false;
